import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

/**
 * CheckoutCompletePage
 * 
 * Simple page that handles redirects from Paddle's hosted checkout.
 * Users are redirected here after completing or canceling their payment.
 * 
 * URL parameters:
 * - status: 'success' or 'cancelled'
 * - _ptxn: Paddle transaction ID (optional)
 */
const CheckoutCompletePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const status = searchParams.get('status');
  const transactionId = searchParams.get('_ptxn');

  useEffect(() => {
    console.log('📥 Checkout complete redirect received:', { status, transactionId });

    if (status === 'success') {
      // Success - show success message briefly then redirect to dashboard
      setTimeout(() => {
        navigate('/?subscription=success');
      }, 2000);
    } else if (status === 'cancelled') {
      // Cancelled - redirect back to dashboard
      setTimeout(() => {
        navigate('/?subscription=cancelled');
      }, 1500);
    } else {
      // Unknown status - redirect to dashboard after brief delay
      setTimeout(() => {
        navigate('/');
      }, 1000);
    }
  }, [status, transactionId, navigate]);

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-dark-900 to-gray-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <span className="text-4xl">🎉</span>
          </div>
          <h2 className="font-orbitron text-white text-3xl font-bold mb-4">
            Payment Successful!
          </h2>
          <p className="font-rajdhani text-gray-300 text-lg mb-6">
            Thank you for your subscription. Your account is being activated...
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-system-blue mx-auto"></div>
          <p className="font-rajdhani text-gray-500 text-sm mt-4">
            Redirecting to dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-dark-900 to-gray-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">❌</span>
          </div>
          <h2 className="font-orbitron text-white text-2xl font-bold mb-4">
            Checkout Cancelled
          </h2>
          <p className="font-rajdhani text-gray-300 mb-6">
            No worries! You can subscribe anytime.
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500 mx-auto"></div>
          <p className="font-rajdhani text-gray-500 text-sm mt-4">
            Redirecting to dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Default loading state
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-dark-900 to-gray-900 flex items-center justify-center px-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-system-blue mx-auto mb-4"></div>
        <p className="font-rajdhani text-white text-lg">Processing...</p>
      </div>
    </div>
  );
};

export default CheckoutCompletePage;

